import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

export function ErrorBox({ children, retry }: { children: ReactNode; retry?: () => void }) {
  return (
    <div className="error" role="alert">
      {children}
      {retry && (
        <button className="btn" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>
}

export function Loading() {
  return (
    <div className="empty muted" role="status">
      <span className="loading-dot" /> Loading…
    </div>
  )
}

export function StatusPill({ status }: { status: string }) {
  const cls =
    status === 'active' || status === 'live'
      ? 'pill pill-active'
      : ['provisioning', 'deploying', 'paused', 'suspended'].includes(status)
        ? 'pill pill-warn'
        : ['failed', 'deleting', 'deleted', 'revoked'].includes(status)
          ? 'pill pill-dead'
          : 'pill'
  return <span className={cls}>{status}</span>
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string
  description?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {description && <p className="sub">{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  )
}

export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const timer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(timer.current), [])
  return (
    <span className="copy-control">
      <button
        type="button"
        className="btn btn-small"
        onClick={async () => {
          clearTimeout(timer.current)
          try {
            await navigator.clipboard.writeText(value)
            setState('copied')
          } catch {
            setState('failed')
          }
          timer.current = setTimeout(() => setState('idle'), 3000)
        }}
      >
        {state === 'copied' ? 'Copied' : label}
      </button>
      <span role="status" className={state === 'failed' ? 'copy-error' : 'sr-only'}>
        {state === 'failed'
          ? 'Could not copy. Select and copy the text manually.'
          : state === 'copied'
            ? 'Copied to clipboard'
            : ''}
      </span>
    </span>
  )
}

/** Native modal provides focus trapping, Escape and focus restoration. */
export function Dialog({
  open,
  title,
  onClose,
  busy = false,
  children,
}: {
  open: boolean
  title: string
  onClose: () => void
  busy?: boolean
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  useEffect(() => {
    const dialog = ref.current!
    if (open && !dialog.open) dialog.showModal()
    else if (!open && dialog.open) dialog.close()
  }, [open])
  return (
    <dialog
      ref={ref}
      className="modal"
      aria-labelledby={titleId}
      aria-busy={busy}
      onCancel={(e) => {
        e.preventDefault()
        if (!busy) onClose()
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose()
      }}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <div className="dialog-head">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="btn btn-small"
            aria-label="Close dialog"
            disabled={busy}
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </dialog>
  )
}

export function relative(iso: string): string {
  const then = new Date(iso).getTime()
  const secs = Math.round((Date.now() - then) / 1000)
  if (secs < 60) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

/**
 * The forward-looking twin of relative(). Kept separate rather than folded
 * into it: relative() subtracts in the other direction, so a future date runs
 * through it as "-7d ago", which reads as a bug to whoever sees it.
 */
export function until(iso: string): string {
  const secs = Math.round((new Date(iso).getTime() - Date.now()) / 1000)
  if (secs <= 0) return 'now'
  if (secs < 60) return 'in under a minute'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `in ${mins}m`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `in ${hrs}h`
  return `in ${Math.round(hrs / 24)}d`
}
